import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Controller, useForm } from 'react-hook-form'
import RoundedModal from '../../../../../molecules/RoundedModal'
import SydLabel, { hookFormErrorAdapter } from '../../../../../commonDesign/SydLabel'
import SydInput from '../../../../../commonDesign/SydInput'
import SydSelect from '../../../../../commonDesign/SydSelect'
import Switch from '../../../../../molecules/Switch'
import { useCreateStatusTemplateMetadataTypeMutation } from '../../../../../../api/groups'
import SydTextArea from '../../../../../commonDesign/SydTextArea'
import SydModalActions from '../../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../../commonDesign/Button'

const VALUE_TYPE_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes/No' }
]

function AddMetadataTypeDialog ({ open, onClose, templateId }) {
  const { mutateAsync: createMetadataType, isLoading } = useCreateStatusTemplateMetadataTypeMutation()

  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      metadataKey: '',
      displayName: '',
      description: '',
      valueType: 'date',
      ordinal: 1,
      showInListPage: false
    }
  })
  const { handleSubmit, reset } = form

  const handleClose = useCallback(() => {
    reset()
    onClose()
  }, [reset, onClose])

  const onSubmit = useCallback(async (formData) => {
    try {
      await createMetadataType({
        statusTemplateId: templateId,
        metadataKey: formData.metadataKey,
        displayName: formData.displayName,
        description: formData.description || null,
        valueType: formData.valueType,
        ordinal: parseInt(formData.ordinal, 10),
        showInListPage: formData.showInListPage
      })
      handleClose()
    } catch (error) {
      console.error('Failed to create metadata type:', error)
    }
  }, [createMetadataType, templateId, handleClose])

  return (
    <RoundedModal
      title='Add Metadata Field'
      subtitle='Add a new metadata field to this status report template to be used on individual status report items.'
      open={open}
      onClose={handleClose}
      position='top'
    >
      <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
        <Controller
          name='metadataKey'
          control={form.control}
          rules={{ required: { value: true, message: 'Key is Required' } }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Key'
              required
              description='Unique identifier'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput
                {...field}
                disabled={isLoading}
                size='sm'
              />
            </SydLabel>
          )}
        />

        <Controller
          name='displayName'
          control={form.control}
          rules={{ required: { value: true, message: 'Display Name is Required' } }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Display Name'
              required
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydInput
                disabled={isLoading}
                {...field}
                size='sm'
              />
            </SydLabel>
          )}
        />

        <Controller
          name='description'
          control={form.control}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Description'
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydTextArea
                {...field}
                disabled={isLoading}
                rows={4}
              />
            </SydLabel>
          )}
        />

        <Controller
          name='valueType'
          control={form.control}
          rules={{ required: { value: true, message: 'Value Type is Required' } }}
          render={({ field, fieldState }) => (
            <SydLabel
              label='Value Type'
              required
              error={hookFormErrorAdapter(form, fieldState)}
            >
              <SydSelect
                {...field}
                disabled={isLoading}
                options={VALUE_TYPE_OPTIONS}
                size='sm'
              />
            </SydLabel>
          )}
        />

        <div className='flex gap-5'>
          <Controller
            name='ordinal'
            control={form.control}
            render={({ field, fieldState }) => (
              <SydLabel
                label='Display Order'
                description='Lower numbers appear first'
                error={hookFormErrorAdapter(form, fieldState)}
              >
                <SydInput
                  {...field}
                  disabled={isLoading}
                  type='number'
                  size='sm'
                />
              </SydLabel>
            )}
          />

          <Controller
            name='showInListPage'
            control={form.control}
            render={({ field, fieldState }) => (
              <div
                className='row flex gap-2 items-center'
                style={{ cursor: 'pointer', pointerEvents: isLoading ? 'none' : 'auto' }}
                onClick={() => field.onChange(!field.value)}
              >
                <Switch
                  {...field}
                  checked={field.value}
                  size='sm'
                  disabled={isLoading}
                />
                <SydLabel
                  label='Show In List Page'
                  description='Will surface this value (if set) in the list of data'
                  error={hookFormErrorAdapter(form, fieldState)}
                />
              </div>
            )}
          />
        </div>

        <SydModalActions>
          <SydButton
            onClick={handleClose}
            variant='outline'
          >
            Cancel
          </SydButton>
          <SydButton
            type='submit'
            variant='primary'
            processing={isLoading}
          >
            Add Metadata Field
          </SydButton>
        </SydModalActions>
      </form>
    </RoundedModal>
  )
}

AddMetadataTypeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  templateId: PropTypes.number.isRequired
}

export default AddMetadataTypeDialog
